import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, Button, IconButton,
    Typography, Box, Slider, Chip, TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Activity, Thermometer, Gauge, Wind, Pill } from 'lucide-react';
import "./VitalModal.scss";

const VitalsDialog = ({ open, handleClose, activeItem, onConfirm }: any) => {
    const [temp, setTemp] = useState(98.6);
    const [systolic, setSystolic] = useState(120);
    const [diastolic, setDiastolic] = useState(80);
    const [respiration, setRespiration] = useState(16);
    const [heartRate, setHeartRate] = useState(72);
    const [medication, setMedication] = useState("");

    const getHeartRateColor = (bpm: number) => (bpm < 60 || bpm > 100 ? '#ef4444' : '#22c55e');
    const getTempColor = (f: number) => {
        if (f > 100.4) return '#ef4444';
        if (f < 97.0) return '#3b82f6';
        return '#f59e0b';
    };
    const getRespColor = (rpm: number) => (rpm < 12 || rpm > 20 ? '#ef4444' : '#0ea5e9');

    useEffect(() => {
        if (open) {
            setTemp(98.6);
            setSystolic(120);
            setDiastolic(80);
            setRespiration(16);
            setHeartRate(72);
            setMedication("");
        }
    }, [open, activeItem?.key]);

    const handleConfirmSelection = () => {
        let finalValue = "";
        switch (activeItem?.key) {
            case 'hr': finalValue = `${heartRate} BPM`; break;
            case 'temp': finalValue = `${temp.toFixed(1)}°F`; break;
            case 'bp': finalValue = `${systolic}/${diastolic} mmHg`; break;
            case 'resp': finalValue = `${respiration} rpm`; break;
            case 'med': finalValue = medication; break;
            default: finalValue = "N/A";
        }

        onConfirm({
            key: activeItem.key,
            title: activeItem.title,
            value: finalValue,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        });
        handleClose();
    };

    const renderSliderField = (label: string, value: number, unit: string, color: string, min: number, max: number, onChange: any, icon: any, statusText: string) => (
        <Box sx={{ textAlign: 'center', py: 1 }}>
            <Box sx={{ mb: 2 }}>
                <Typography sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
                    {label}
                </Typography>
                <Typography sx={{ color: color, fontSize: '2.5rem', fontWeight: 800, fontFamily: 'monospace' }}>
                    {value} <span style={{ fontSize: '1rem', color: '#94a3b8' }}>{unit}</span>
                </Typography>
            </Box>

            <Box sx={{ position: 'relative', height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                <Box className="pulse-ring" sx={{ position: 'absolute', width: 60, height: 60, borderRadius: '50%', background: color, opacity: 0.1, filter: 'blur(15px)' }} />
                {icon}
            </Box>

            <Slider
                value={value}
                min={min}
                max={max}
                step={label === 'Temperature' ? 0.1 : 1}
                onChange={onChange}
                sx={{
                    color: color,
                    height: 8,
                    '& .MuiSlider-thumb': {
                        height: 24, width: 24, backgroundColor: '#fff', border: `3px solid ${color}`,
                        boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                    }
                }}
            />
            <Chip label={statusText} size="small" sx={{ mt: 2, bgcolor: `${color}15`, color: color, fontWeight: 700, border: `1px solid ${color}30` }} />
        </Box>
    );

    const renderContent = () => {
        switch (activeItem?.key) {
            case 'hr':
                return renderSliderField('Heart Rate', heartRate, 'BPM', getHeartRateColor(heartRate), 40, 180, (e: any, v: number) => setHeartRate(v), <Activity size={52} color={getHeartRateColor(heartRate)} />, heartRate > 100 ? 'High (Tachycardia)' : heartRate < 60 ? 'Low (Bradycardia)' : 'Normal Range');
            case 'temp':
                return renderSliderField('Temperature', Number(temp.toFixed(1)), '°F', getTempColor(temp), 95, 105, (e: any, v: number) => setTemp(v), <Thermometer size={52} color={getTempColor(temp)} />, temp > 100.4 ? 'Fever Detected' : temp < 97 ? 'Low Temp' : 'Normal');
            case 'bp':
                const isHighBP = systolic >= 130 || diastolic >= 80;
                const bpColor = isHighBP ? '#f59e0b' : '#22c55e';
                return (
                    <Box sx={{ py: 1, textAlign: 'center' }}>
                        <Typography sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem', mb: 1 }}>Blood Pressure</Typography>
                        <Typography sx={{ color: '#1e293b', fontSize: '2.5rem', fontWeight: 800, fontFamily: 'monospace' }}>
                            {systolic}<span style={{ color: '#cbd5e1' }}>/</span>{diastolic} <span style={{ fontSize: '1rem', color: '#94a3b8' }}>mmHg</span>
                        </Typography>
                        <Box sx={{ my: 3 }}><Gauge size={52} color={bpColor} /></Box>
                        <Typography variant="caption" sx={{ display: 'block', mb: 0.5, textAlign: 'left', color: '#64748b' }}>Systolic</Typography>
                        <Slider value={systolic} min={80} max={200} onChange={(e, v) => setSystolic(v as number)} sx={{ color: bpColor, mb: 2 }} />
                        <Typography variant="caption" sx={{ display: 'block', mb: 0.5, textAlign: 'left', color: '#64748b' }}>Diastolic</Typography>
                        <Slider value={diastolic} min={40} max={120} onChange={(e, v) => setDiastolic(v as number)} sx={{ color: bpColor }} />
                    </Box>
                );
            case 'med':
                return (
                    <Box sx={{ py: 1 }}>

                       Enter the medication name and dosage.
                        <TextField

                            style={{
                                border: '1px solid gray',marginTop:'5px',
                                borderRadius: '15px'
                            }}
                            fullWidth autoFocus placeholder="e.g. Paracetamol 500mg"
                            value={medication} onChange={(e) => setMedication(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 3,
                                    '& fieldset': { borderColor: 'primary.main' },
                                    '&.Mui-focused fieldset': { borderColor: 'secondary.main' },
                                },
                            }}
                        />
                    </Box>
                );
            default:
                return <Typography sx={{ py: 4, textAlign: 'center', color: 'gray' }}>UI coming soon...</Typography>;
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: '24px', p: 1, boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)' } }}>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}> {activeItem?.title}</Typography>
                <IconButton onClick={handleClose} size="small" sx={{
                    bgcolor: '#f1f5f9',
                    color: 'black !important', // Sets the icon color
                    '&:hover': {
                        bgcolor: '#e2e8f0', // Slightly darker grey on hover
                        color: '#ef4444',   // Optional: turns red on hover for a "close" feel
                    }
                }}><CloseIcon fontSize="small" /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ mt: 2 }}>{renderContent()}</DialogContent>
            <Box sx={{ p: 2, pt: 0 }}>
                <Button
                    fullWidth onClick={handleConfirmSelection} variant="contained" disableElevation
                    disabled={activeItem?.key === 'med' && !medication}
                    sx={{ bgcolor: '#1a237e', borderRadius: '14px', py: 1.8, textTransform: 'none', fontWeight: 700, fontSize: '1rem', '&:hover': { bgcolor: '#0d1440' } }}
                >
                    Confirm Record
                </Button>
            </Box>
        </Dialog>
    );
};

export default VitalsDialog;